import type { CollagePhoto, CollageSlotGeom, CollageSlotInput } from './collage.js';
import { collageAutoCrop } from './collage-crop.js';

/**
 * Automatic placement of collage photos on slots ("Kutije sa slikama").
 *
 * A Kuhn–Munkres assignment: every photo against every slot, one cost per
 * pair, the arrangement with the lowest total wins. The cost says what a
 * printer would say — a landscape photo does not go into a tall slot, a face
 * is not cut, a small phone photo is not blown up into the hero slot, the
 * sharpest photos take the biggest slots. A seeded random term then decides
 * between arrangements that are equally good, which is the owner's "random
 * places" without chance ever overriding shape.
 *
 * Orientation is a tier, not a price: a landscape photo in a tall slot (or a
 * tall photo in a wide one) costs more than every other term of the whole
 * arrangement can add up to, so the plan first uses as few such placements as
 * the photos allow — for every seed the same number, the smallest — and only
 * then weighs faces, resolution, size and chance. A plain price could not
 * promise that: a swap changes two pairs, so the noise difference reaches
 * twice its range, and the size-rank term can lean the wrong way as well.
 *
 * Seed 0 is deterministic best fit (tests, agent review renders); the widget
 * draws a random seed for the first arrangement and a new one for "Promešaj".
 * Pure: no DOM, no Node, no randomness except the seeded one.
 */

/** Weight of the shape mismatch; a 4:3 photo in a 3:4 slot costs 0.875. */
const ASPECT_WEIGHT = 2;
/** Per cut face, at most three counted. */
const FACE_WEIGHT = 1.5;
/** Bigger photo → bigger slot. */
const RANK_WEIGHT = 0.5;
/**
 * The random term's range. It only reorders arrangements with the same number
 * of orientation mismatches (the tier above every other term), so it can
 * trade a little shape, size or sharpness for variety, never a turned photo.
 */
export const COLLAGE_NOISE = 0.6;
/** Photos and slots within this factor of square have no orientation: they fit either way. */
export const COLLAGE_SQUARE_BAND = 1.05;
/** The print floor the widget's quality advice uses (photoquality.ts FLOOR_PPI). */
const FLOOR_PPI = 100;

/**
 * Mulberry32: a tiny seeded PRNG, uniform in [0, 1). The same seed gives the
 * same sequence on every device and in every runtime.
 */
export function mulberry32(seed: number): () => number {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/** FNV-1a, 32 bit. */
function hash32(text: string): number {
  let h = 0x811c9dc5;
  for (let i = 0; i < text.length; i++) {
    h ^= text.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  return h >>> 0;
}

/**
 * The random term for one photo in one slot, in [0, 1): keyed by the ids, not
 * by position, so adding a photo or fixing a slot does not reshuffle the noise
 * of every other pair — a re-run with the same seed moves only what changed.
 * Seed 0 gives 0.
 */
export function collageNoise(seed: number, assetId: string, elementId: string): number {
  if (!seed) return 0;
  return mulberry32((seed ^ hash32(assetId) ^ Math.imul(hash32(elementId), 0x9e3779b1)) >>> 0)();
}

/**
 * Minimum-cost assignment (Kuhn–Munkres with potentials, O(n²·m)).
 * `cost` is rows × columns with rows ≤ columns; returns the column of each row.
 * Deterministic: equal costs resolve the same way every time. Non-finite costs
 * are treated as very expensive rather than breaking the search.
 */
export function solveAssignment(cost: readonly (readonly number[])[]): number[] {
  const n = cost.length;
  if (n === 0) return [];
  const m = cost[0]!.length;
  if (m < n) throw new Error('solveAssignment needs at least as many columns as rows');
  const at = (i: number, j: number) => {
    const c = cost[i]![j]!;
    return Number.isFinite(c) ? c : 1e9;
  };
  const u = new Float64Array(n + 1);
  const v = new Float64Array(m + 1);
  const p = new Int32Array(m + 1);
  const way = new Int32Array(m + 1);
  const minv = new Float64Array(m + 1);
  const used = new Uint8Array(m + 1);
  for (let i = 1; i <= n; i++) {
    p[0] = i;
    let j0 = 0;
    minv.fill(Infinity);
    used.fill(0);
    do {
      used[j0] = 1;
      const i0 = p[j0]!;
      let delta = Infinity;
      let j1 = 0;
      for (let j = 1; j <= m; j++) {
        if (used[j]) continue;
        const cur = at(i0 - 1, j - 1) - u[i0]! - v[j]!;
        if (cur < minv[j]!) {
          minv[j] = cur;
          way[j] = j0;
        }
        if (minv[j]! < delta) {
          delta = minv[j]!;
          j1 = j;
        }
      }
      for (let j = 0; j <= m; j++) {
        if (used[j]) {
          u[p[j]!] = u[p[j]!]! + delta;
          v[j] = v[j]! - delta;
        } else {
          minv[j] = minv[j]! - delta;
        }
      }
      j0 = j1;
    } while (p[j0] !== 0);
    do {
      const j1 = way[j0]!;
      p[j0] = p[j1]!;
      j0 = j1;
    } while (j0 !== 0);
  }
  const out = new Array<number>(n).fill(-1);
  for (let j = 1; j <= m; j++) if (p[j]) out[p[j]! - 1] = j - 1;
  return out;
}

/**
 * Fills in what a slot list may leave out: position in the list, area, size
 * rank (0 = smallest … 1 = biggest, ties share a rank), print dpi.
 */
export function completeCollageSlots(slots: readonly CollageSlotInput[]): CollageSlotGeom[] {
  const seen = new Set<string>();
  const list = slots.filter((s) => {
    if (seen.has(s.elementId) || !(s.w > 0 && s.h > 0)) return false;
    seen.add(s.elementId);
    return true;
  });
  const ranks = percentileRanks(list.map((s) => s.w * s.h));
  return list.map((s, i) => ({
    elementId: s.elementId,
    index: s.index ?? i,
    w: s.w,
    h: s.h,
    area: s.w * s.h,
    sizeRank: typeof s.sizeRank === 'number' && Number.isFinite(s.sizeRank) ? s.sizeRank : ranks[i]!,
    ...(typeof s.cx === 'number' && typeof s.cy === 'number' ? { cx: s.cx, cy: s.cy } : {}),
    dpi: s.dpi && s.dpi > 0 ? s.dpi : 300,
    ...(s.sideId ? { sideId: s.sideId } : {}),
    ...(s.shape ? { shape: s.shape } : {}),
  }));
}

/** 0 … 1 by value, ties averaged; a single value ranks 1 (it is the biggest). */
export function percentileRanks(values: readonly number[]): number[] {
  const n = values.length;
  if (n === 0) return [];
  if (n === 1) return [1];
  return values.map((v) => {
    let below = 0;
    let equal = 0;
    for (const o of values) {
      if (o < v) below++;
      else if (o === v) equal++;
    }
    return (below + (equal - 1) / 2) / (n - 1);
  });
}

function sized(photo: CollagePhoto): photo is CollagePhoto & { w: number; h: number } {
  return typeof photo.w === 'number' && typeof photo.h === 'number' && photo.w > 0 && photo.h > 0;
}

/** 1 wide, −1 tall, 0 square (within `COLLAGE_SQUARE_BAND`). */
function orientation(w: number, h: number): -1 | 0 | 1 {
  const r = w / h;
  return r > COLLAGE_SQUARE_BAND ? 1 : r < 1 / COLLAGE_SQUARE_BAND ? -1 : 0;
}

/**
 * True when the photo and the slot face opposite ways — a landscape photo in
 * a tall slot or a tall photo in a wide one. Square-ish photos and slots
 * (within 5 %) and photos of unknown size never mismatch. Placement and
 * "Ponovi slike" keep these to the unavoidable minimum.
 */
export function collageOrientationMismatch(photo: CollagePhoto, slot: { w: number; h: number }): boolean {
  if (!sized(photo) || !(slot.w > 0 && slot.h > 0)) return false;
  return orientation(photo.w, photo.h) * orientation(slot.w, slot.h) < 0;
}

/**
 * What putting `photo` into `slot` costs on its own merits (no rank, no noise):
 * shape mismatch, faces cut by the automatic framing, and print resolution
 * short of the slot. A photo of unknown size costs nothing for shape and
 * resolution — it is placed by what is known.
 */
export function collageFitCost(
  photo: CollagePhoto,
  slot: Pick<CollageSlotGeom, 'w' | 'h'> & { dpi?: number },
  options: { faceFraming?: boolean } = {},
): number {
  if (!sized(photo)) return 0;
  const rp = photo.w / photo.h;
  const rs = slot.w / slot.h;
  let cost = ASPECT_WEIGHT * (1 - Math.min(rp, rs) / Math.max(rp, rs));
  if (options.faceFraming !== false && photo.faces && photo.faces.length > 0) {
    cost += FACE_WEIGHT * Math.min(3, collageAutoCrop(photo, slot, options).facesCut);
  }
  // Source pixels per scene pixel at cover fit, zoom 1 (the widget's
  // assessResolution ratio); the scene's dpi is what the slot prints at.
  const ratio = Math.min(photo.w / slot.w, photo.h / slot.h);
  const floor = FLOOR_PPI / (slot.dpi && slot.dpi > 0 ? slot.dpi : 300);
  if (ratio < floor) cost += 1 + 3 * (floor - ratio);
  else if (ratio < 1) cost += 0.4 * (1 - ratio);
  return cost;
}

export interface CollagePlanOptions {
  /** 0 or absent: deterministic best fit. Otherwise the arrangement's seed. */
  seed?: number;
  /** Slots that keep their photo (elementId → assetId); copied into the plan untouched. */
  fixed?: Readonly<Record<string, string>>;
  /** false: faces play no part (the question's `faceFraming`). Default true. */
  faceFraming?: boolean;
}

export interface CollagePlan {
  /** elementId → assetId for every filled slot, fixed ones included. */
  bySlot: Record<string, string>;
  /** Photos that got no slot, in pool order: the lowest resolution, latest picked. */
  spares: string[];
}

export interface CollageCostMatrix {
  /** Square: rows are photos then empty-slot dummies, columns slots then spare dummies. */
  cost: number[][];
  /** Asset id per row, null for a dummy ("this slot stays empty"). */
  photoIds: Array<string | null>;
  /** Element id per column, null for a dummy ("this photo is a spare"). */
  slotIds: Array<string | null>;
}

interface Prepared {
  photos: CollagePhoto[];
  slots: CollageSlotGeom[];
  fixed: Record<string, string>;
}

function prepare(photos: readonly CollagePhoto[], slots: readonly CollageSlotInput[], options: CollagePlanOptions): Prepared {
  const all = completeCollageSlots(slots);
  const fixed: Record<string, string> = {};
  for (const [elementId, assetId] of Object.entries(options.fixed ?? {})) {
    if (typeof assetId === 'string' && assetId) fixed[elementId] = assetId;
  }
  const fixedAssets = new Set(Object.values(fixed));
  const seen = new Set<string>();
  const free = photos.filter((p) => {
    if (!p || typeof p.assetId !== 'string' || seen.has(p.assetId) || fixedAssets.has(p.assetId)) return false;
    seen.add(p.assetId);
    return true;
  });
  return { photos: free, slots: all.filter((s) => !Object.prototype.hasOwnProperty.call(fixed, s.elementId)), fixed };
}

/**
 * The placement cost matrix, exposed so tests (and anyone curious) can check
 * the assignment against brute force.
 *
 * Real pair: `tier·[opposite orientation] + 2·aspectLoss
 * + 1.5·min(3, facesCut) + resolution deficit + 0.5·|resRank − sizeRank|
 * + noise`, where noise is `U[0, 0.6)` for a seed and a tiny
 * keep-the-pick-order tie-break for seed 0. Too few photos: a dummy photo
 * costs `3·sizeRank`, so the smallest slots stay empty. Too many: a spare
 * costs `0.8·resRank + 0.01·(1 − i/n)`, so the lowest-resolution,
 * latest-picked photos wait in "Rezerva".
 *
 * `tier` is `n·(largest other entry + 1) + 1`: more than the other terms of
 * any two whole arrangements can differ by, so an arrangement with fewer
 * orientation mismatches always costs less, whatever the seed.
 */
export function collageCostMatrix(
  photos: readonly CollagePhoto[],
  slots: readonly CollageSlotInput[],
  options: CollagePlanOptions = {},
): CollageCostMatrix {
  return matrixOf(prepare(photos, slots, options), options);
}

function matrixOf(prep: Prepared, options: CollagePlanOptions): CollageCostMatrix {
  const { photos, slots } = prep;
  const P = photos.length;
  const S = slots.length;
  const n = Math.max(P, S);
  const seed = Number.isFinite(options.seed) ? (options.seed! | 0) : 0;
  const resRanks = resolutionRanks(photos);
  const cost: number[][] = [];
  const turned: Array<[number, number]> = [];
  let largest = 0;
  for (let i = 0; i < n; i++) {
    const row = new Array<number>(n);
    for (let j = 0; j < n; j++) {
      if (i < P && j < S) {
        const photo = photos[i]!;
        const slot = slots[j]!;
        const tie = seed !== 0
          ? COLLAGE_NOISE * collageNoise(seed, photo.assetId, slot.elementId)
          : 1e-4 * Math.abs((i + 0.5) / P - (j + 0.5) / S);
        row[j] = collageFitCost(photo, slot, options) + RANK_WEIGHT * Math.abs(resRanks[i]! - slot.sizeRank) + tie;
        if (collageOrientationMismatch(photo, slot)) turned.push([i, j]);
      } else if (i < P) {
        row[j] = 0.8 * resRanks[i]! + 0.01 * (1 - i / P);
      } else if (j < S) {
        row[j] = 3 * slots[j]!.sizeRank;
      } else {
        row[j] = 0;
      }
      if (Number.isFinite(row[j]!)) largest = Math.max(largest, row[j]!);
    }
    cost.push(row);
  }
  // Every entry is ≥ 0, so two arrangements' other terms differ by at most
  // n·largest: one mismatch more always costs more than that.
  const tier = n * (largest + 1) + 1;
  for (const [i, j] of turned) cost[i]![j] = cost[i]![j]! + tier;
  return {
    cost,
    photoIds: Array.from({ length: n }, (_, i) => (i < P ? photos[i]!.assetId : null)),
    slotIds: Array.from({ length: n }, (_, j) => (j < S ? slots[j]!.elementId : null)),
  };
}

/** Pixel-count rank 0 … 1; a photo of unknown size sits in the middle. */
function resolutionRanks(photos: readonly CollagePhoto[]): number[] {
  const known = photos.map((p, i) => ({ i, px: sized(p) ? p.w * p.h : undefined }));
  const withSize = known.filter((k) => k.px !== undefined);
  const ranks = percentileRanks(withSize.map((k) => k.px!));
  const out = new Array<number>(photos.length).fill(0.5);
  withSize.forEach((k, r) => {
    out[k.i] = ranks[r]!;
  });
  return out;
}

/**
 * Which photo goes into which slot. Fixed slots keep their photo; the other
 * photos are assigned to the other slots by the cost above. Deterministic
 * for a seed; 60 photos on 60 slots solve in a few milliseconds.
 */
export function planCollagePlacement(
  photos: readonly CollagePhoto[],
  slots: readonly CollageSlotInput[],
  options: CollagePlanOptions = {},
): CollagePlan {
  const prep = prepare(photos, slots, options);
  const bySlot: Record<string, string> = { ...prep.fixed };
  const spares: string[] = [];
  if (prep.photos.length === 0) return { bySlot, spares };
  if (prep.slots.length === 0) return { bySlot, spares: prep.photos.map((p) => p.assetId) };
  const { cost, photoIds, slotIds } = matrixOf(prep, options);
  const assignment = solveAssignment(cost);
  const placed = new Set<string>();
  assignment.forEach((j, i) => {
    const assetId = photoIds[i];
    const elementId = slotIds[j];
    if (assetId && elementId) {
      bySlot[elementId] = assetId;
      placed.add(assetId);
    }
  });
  for (const photo of prep.photos) if (!placed.has(photo.assetId)) spares.push(photo.assetId);
  return { bySlot, spares };
}
